import {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
  type ChangeEvent,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { AudioTrack, useLocalParticipant, useRoomContext, useTracks } from '@livekit/components-react';
import type { TrackReference } from '@livekit/components-core';
import { RemoteParticipant, Track, RoomEvent, ParticipantEvent, type LocalParticipant } from 'livekit-client';
import { logVideoDiagnostic, type VideoDiagnosticContext } from './videoDiagnostics';
import { useRoom } from '../room/RoomProvider';
import {
  getPreferredCameraDeviceId,
  getPreferredMicrophoneDeviceId,
  setPreferredCameraDeviceId,
  setPreferredMicrophoneDeviceId,
} from './cameraDevicePreference';
import { HOST_BROWSER_AUDIO_TRACK_NAME } from '../audio/browserAudio.constants';
import { DEFAULT_HI_RES_TUNING_SETTINGS } from './HiResTuningPanel';
import { getCameraEnableOptions, getParticipantCameraProfile, type LiveKitCameraProfile } from './liveKitCameraProfiles';
import { getMediaDeviceLabel, getPublishedCameraDevice, waitForPublishedCameraDevice } from './cameraDeviceSelection';
import {
  dispatchCameraDeviceSwitchFinished,
  dispatchCameraDeviceSwitchStarted,
  dispatchCameraDeviceTrackRefreshed,
} from './cameraDeviceEvents';
import { useMicrophoneSpeechActivity } from './useMicrophoneSpeechActivity';

interface MediaControlsProps {
  className?: string;
  canPinLocalCamera?: boolean;
  isLocalCameraPinned?: boolean;
  onToggleLocalCameraPin?: () => void;
  showMutedSpeakingWarning?: boolean;
}

const BROWSER_AUDIO_VOLUME_STORAGE_KEY = 'drawclub.browserAudioVolume';
const DEFAULT_BROWSER_AUDIO_VOLUME = 1;
const BROWSER_AUDIO_DUCKED_VOLUME_MULTIPLIER = 0.05;
const BROWSER_AUDIO_DUCK_ATTACK_MS = 450;
const BROWSER_AUDIO_DUCK_RELEASE_MS = 1800;
const BROWSER_AUDIO_DRAW_BREAK_FADE_MS = 2400;
const BROWSER_AUDIO_VOLUME_PREVIEW_HOLD_MS = 900;
const BROWSER_AUDIO_VOLUME_PREVIEW_RELEASE_MS = 800;
const BROWSER_AUDIO_WEB_AUDIO_GAIN_TIME_CONSTANT_SECONDS = 0.09;
const MUTED_MIC_ANALYSIS_INTERVAL_MS = 120;
const MUTED_MIC_SPEAKING_RMS_THRESHOLD = 0.018;
const MUTED_MIC_SPEAKING_HOLD_MS = 1800;
const MUTED_SPEAKING_TOOLTIP_SHOW_DELAY_MS = 180;
const MUTED_SPEAKING_TOOLTIP_MIN_VISIBLE_MS = 1400;
const MUTED_SPEAKING_TOOLTIP_FADE_MS = 220;
const BROWSER_AUDIO_BREAK_QUIET_HINT = 'Music is quiet during breaks. Drag the slider to hear the normal volume.';
const browserAudioPlaybackContexts = new Set<AudioContext>();

type BrowserAudioVolumeTransitionReason =
  | 'initial'
  | 'draw-break'
  | 'speech-duck'
  | 'volume-preview'
  | 'manual-volume'
  | 'target-change'
  | 'refresh';

type BrowserAudioVolumeDecisionReason =
  | 'muted'
  | 'raw-volume-preview'
  | 'speech-duck'
  | 'break-duck'
  | 'speech-and-break-duck'
  | 'normal';

type BrowserAudioVolumePreviewTrigger =
  | 'slider-pointer-start'
  | 'slider-touch-start'
  | 'slider-change'
  | 'slider-pointer-end'
  | 'slider-touch-end'
  | 'slider-blur';

interface BrowserAudioVolumeTransitionSnapshot {
  userVolume: number;
  targetVolume: number;
  isMicrophoneDucking: boolean;
  shouldLowerForSessionState: boolean;
  isPreviewingRawVolume: boolean;
  isActiveDrawingPose: boolean;
  decisionReason: BrowserAudioVolumeDecisionReason;
}

function resumeBrowserAudioPlaybackContexts() {
  browserAudioPlaybackContexts.forEach((audioContext) => {
    if (audioContext.state === 'suspended') {
      void audioContext.resume().catch(() => undefined);
    }
  });
}

export function MediaControls({
  className = '',
  canPinLocalCamera = false,
  isLocalCameraPinned = false,
  onToggleLocalCameraPin,
  showMutedSpeakingWarning = true,
}: MediaControlsProps) {
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();
  const {
    roomCode,
    isHost,
    browserAudio,
    browserAudioSettings,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
    poseTimer,
    participants: sessionParticipants,
  } = useRoom();
  const browserAudioTracks = useTracks([Track.Source.ScreenShareAudio], { onlySubscribed: false });
  const microphoneTracks = useTracks([Track.Source.Microphone], { onlySubscribed: false });
  const [canPlayAudio, setCanPlayAudio] = useState(true);
  const [showMicMenu, setShowMicMenu] = useState(false);
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [showBrowserAudioVolume, setShowBrowserAudioVolume] = useState(false);
  const [browserAudioVolume, setBrowserAudioVolume] = useState(getStoredBrowserAudioVolume);
  const [isPreviewingBrowserAudioVolume, setIsPreviewingBrowserAudioVolume] = useState(false);
  const [renderedLiveKitBrowserAudioVolume, setRenderedLiveKitBrowserAudioVolume] = useState(0);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedMic, setSelectedMic] = useState<string>('');
  const [pendingCameraDeviceId, setPendingCameraDeviceId] = useState<string | null>(null);
  const [, refreshMediaState] = useState(0);

  const micMenuRef = useRef<HTMLDivElement>(null);
  const micButtonRef = useRef<HTMLButtonElement>(null);
  const cameraMenuRef = useRef<HTMLDivElement>(null);
  const browserAudioControlRef = useRef<HTMLDivElement>(null);
  const liveKitBrowserAudioAnimationRef = useRef<number | null>(null);
  const currentLiveKitBrowserAudioVolumeRef = useRef(0);
  const browserAudioVolumeTransitionRef = useRef<BrowserAudioVolumeTransitionSnapshot | null>(null);
  const browserAudioVolumePreviewActiveRef = useRef(false);
  const browserAudioVolumePreviewEndTimeoutRef = useRef<number | null>(null);

  const context = useMemo(() => ({
    roomCode,
    participantId: localParticipant?.identity ?? null,
    participantName: localParticipant?.name ?? null,
    participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
  }), [localParticipant?.identity, localParticipant?.name, roomCode]);

  // Get track states
  const audioTrack = localParticipant?.getTrackPublication(Track.Source.Microphone);
  const videoTrack = localParticipant?.getTrackPublication(Track.Source.Camera);
  const localBrowserAudioPublication = localParticipant?.getTrackPublication(Track.Source.ScreenShareAudio);
  const localBrowserAudioTrack =
    localBrowserAudioPublication?.trackName === HOST_BROWSER_AUDIO_TRACK_NAME &&
    localBrowserAudioPublication.kind === Track.Kind.Audio &&
    !localBrowserAudioPublication.isMuted
      ? localBrowserAudioPublication.track
      : null;

  const isMicMuted = !audioTrack?.track || audioTrack.isMuted;
  const isCameraOff = !videoTrack?.track || videoTrack.isMuted;
  const isActiveDrawingPose = Boolean(poseTimer?.isActive && !poseTimer.isBreak && !poseTimer.isUntimedBreak);
  const browserAudioTrackRefs = useMemo(() => {
    return browserAudioTracks.filter((trackRef) => {
      if (!(trackRef.participant instanceof RemoteParticipant)) return;
      if (trackRef.publication.kind !== Track.Kind.Audio) return;
      if (trackRef.publication.isMuted) return;
      if (trackRef.publication.trackName !== HOST_BROWSER_AUDIO_TRACK_NAME) return;

      return true;
    });
  }, [browserAudioTracks]);
  const duckingMicrophoneMediaTracks = useMemo(() => {
    const tracks: MediaStreamTrack[] = [];
    const trackIds = new Set<string>();

    const addTrack = (mediaStreamTrack: MediaStreamTrack | null | undefined) => {
      if (!mediaStreamTrack || mediaStreamTrack.readyState !== 'live' || trackIds.has(mediaStreamTrack.id)) {
        return;
      }

      trackIds.add(mediaStreamTrack.id);
      tracks.push(mediaStreamTrack);
    };

    microphoneTracks.forEach((trackRef) => {
      if (trackRef.publication.kind !== Track.Kind.Audio) return;
      if (trackRef.publication.isMuted) return;

      addTrack(trackRef.publication.track?.mediaStreamTrack);
    });

    if (audioTrack && !audioTrack.isMuted) {
      addTrack(audioTrack.track?.mediaStreamTrack);
    }

    return tracks;
  }, [audioTrack, microphoneTracks]);
  const isDuckingMicrophoneSpeaking = useMicrophoneSpeechActivity(duckingMicrophoneMediaTracks, context);
  const hasBrowserAudio = browserAudioTrackRefs.length > 0 || Boolean(localBrowserAudioTrack);
  const browserAudioVolumePercent = Math.round(browserAudioVolume * 100);
  const browserAudioMuted = browserAudioVolume === 0;
  const shouldLowerBrowserAudioForSessionState = browserAudioSettings.lowerMusicDuringBreaks && !isActiveDrawingPose;
  const shouldDuckBrowserAudio = isDuckingMicrophoneSpeaking || shouldLowerBrowserAudioForSessionState;
  const shouldPreviewBrowserAudioRawVolume =
    isPreviewingBrowserAudioVolume &&
    shouldDuckBrowserAudio &&
    !browserAudioMuted;
  const effectiveShouldDuckBrowserAudio = shouldDuckBrowserAudio && !shouldPreviewBrowserAudioRawVolume;
  const browserAudioDuckingMultiplier = effectiveShouldDuckBrowserAudio ? BROWSER_AUDIO_DUCKED_VOLUME_MULTIPLIER : 1;
  const browserAudioOutputVolume = browserAudioVolume * browserAudioDuckingMultiplier;
  const liveKitBrowserAudioVolume = browserAudioMuted ? 0 : browserAudioOutputVolume;
  const browserAudioVolumeDecisionReason = getBrowserAudioVolumeDecisionReason({
    browserAudioMuted,
    isDuckingMicrophoneSpeaking,
    shouldLowerBrowserAudioForSessionState,
    shouldPreviewBrowserAudioRawVolume,
  });
  const poseTimerDiagnostic = useMemo(() => ({
    isActive: poseTimer?.isActive ?? null,
    isPaused: poseTimer?.isPaused ?? null,
    isBreak: poseTimer?.isBreak ?? null,
    isUntimedBreak: poseTimer?.isUntimedBreak ?? null,
    isUntimed: poseTimer?.isUntimed ?? null,
    currentPose: poseTimer?.currentPose ?? null,
    totalPoses: poseTimer?.totalPoses ?? null,
    remaining: poseTimer?.remaining ?? null,
    elapsed: poseTimer?.elapsed ?? null,
    duration: poseTimer?.duration ?? null,
    startedAt: poseTimer?.startedAt ?? null,
    pausedAt: poseTimer?.pausedAt ?? null,
  }), [poseTimer]);
  const browserAudioVolumeTransitionSnapshot = useMemo<BrowserAudioVolumeTransitionSnapshot>(() => ({
    userVolume: browserAudioVolume,
    targetVolume: liveKitBrowserAudioVolume,
    isMicrophoneDucking: isDuckingMicrophoneSpeaking,
    shouldLowerForSessionState: shouldLowerBrowserAudioForSessionState,
    isPreviewingRawVolume: shouldPreviewBrowserAudioRawVolume,
    isActiveDrawingPose,
    decisionReason: browserAudioVolumeDecisionReason,
  }), [
    browserAudioVolumeDecisionReason,
    browserAudioVolume,
    isActiveDrawingPose,
    isDuckingMicrophoneSpeaking,
    liveKitBrowserAudioVolume,
    shouldLowerBrowserAudioForSessionState,
    shouldPreviewBrowserAudioRawVolume,
  ]);
  const browserAudioTrackSignature = useMemo(() => {
    return browserAudioTrackRefs
      .map((trackRef) => [
        trackRef.participant.identity,
        trackRef.publication.trackSid,
        trackRef.publication.trackName,
        trackRef.publication.isMuted ? 'muted' : 'unmuted',
        trackRef.publication.track ? 'track' : 'no-track',
        trackRef.publication.track?.attachedElements.length ?? 0,
      ].join(':'))
      .sort()
      .join('|');
  }, [browserAudioTrackRefs]);
  const duckingMicrophoneTrackSignature = useMemo(() => {
    return duckingMicrophoneMediaTracks
      .map((track) => `${track.id}:${track.readyState}:${track.enabled ? 'enabled' : 'disabled'}`)
      .sort()
      .join('|');
  }, [duckingMicrophoneMediaTracks]);
  const actualCameraDevice = getPublishedCameraDevice(localParticipant);
  const actualCameraDeviceId = actualCameraDevice.deviceId;
  const participantProfileId = useMemo(() => (
    localParticipant?.identity
      ? sessionParticipants.find(participant => participant.participantId === localParticipant.identity)?.profileId ??
        sessionStorage.getItem(`participant-profileId-${roomCode}`)
      : null
  ), [localParticipant?.identity, roomCode, sessionParticipants]);
  const cameraProfile: LiveKitCameraProfile = getParticipantCameraProfile({
    participantId: localParticipant?.identity,
    participantProfileId,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
  });
  const shouldAutoMuteLocalParticipant =
    browserAudioSettings.autoMuteArtistsDuringPoses &&
    isActiveDrawingPose &&
    !isHost &&
    cameraProfile !== 'model';
  const mutedMicrophoneSpeaking = useMutedMicrophoneSpeaking({
    enabled: isMicMuted,
    deviceId: selectedMic,
    context,
  });

  usePoseAutoMuteArtists({
    enabled: shouldAutoMuteLocalParticipant,
    localParticipant,
    isMicMuted,
    context,
  });

  useEffect(() => {
    if (!localParticipant) return;

    const refresh = () => refreshMediaState(version => version + 1);
    localParticipant.on(ParticipantEvent.TrackMuted, refresh);
    localParticipant.on(ParticipantEvent.TrackUnmuted, refresh);
    localParticipant.on(ParticipantEvent.LocalTrackPublished, refresh);
    localParticipant.on(ParticipantEvent.LocalTrackUnpublished, refresh);

    return () => {
      localParticipant.off(ParticipantEvent.TrackMuted, refresh);
      localParticipant.off(ParticipantEvent.TrackUnmuted, refresh);
      localParticipant.off(ParticipantEvent.LocalTrackPublished, refresh);
      localParticipant.off(ParticipantEvent.LocalTrackUnpublished, refresh);
    };
  }, [localParticipant]);

  // Load available devices
  useEffect(() => {
    async function loadDevices() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setMicrophones(devices.filter(d => d.kind === 'audioinput'));
        setCameras(devices.filter(d => d.kind === 'videoinput'));

        const currentAudioTrack = localParticipant?.getTrackPublication(Track.Source.Microphone)?.track;

        if (currentAudioTrack?.mediaStreamTrack) {
          const settings = currentAudioTrack.mediaStreamTrack.getSettings();
          setSelectedMic(settings.deviceId || '');
        } else {
          setSelectedMic(getPreferredMicrophoneDeviceId() || '');
        }
      } catch (error) {
        console.error('Error loading devices:', error);
        logVideoDiagnostic('error', 'media-device-enumeration-failed', { error }, context);
      }
    }

    loadDevices();

    // Refresh device list when devices change
    navigator.mediaDevices.addEventListener('devicechange', loadDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', loadDevices);
    };
  }, [context, localParticipant]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (micMenuRef.current && !micMenuRef.current.contains(e.target as Node)) {
        setShowMicMenu(false);
      }
      if (cameraMenuRef.current && !cameraMenuRef.current.contains(e.target as Node)) {
        setShowCameraMenu(false);
      }
      if (browserAudioControlRef.current && !browserAudioControlRef.current.contains(e.target as Node)) {
        setShowBrowserAudioVolume(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if audio playback is allowed (Chrome autoplay policy)
  useEffect(() => {
    if (!room) return;

    const checkAudioPlayback = () => {
      setCanPlayAudio(room.canPlaybackAudio);
    };

    checkAudioPlayback();
    room.on(RoomEvent.AudioPlaybackStatusChanged, checkAudioPlayback);

    return () => {
      room.off(RoomEvent.AudioPlaybackStatusChanged, checkAudioPlayback);
    };
  }, [room]);

  useEffect(() => {
    try {
      localStorage.setItem(BROWSER_AUDIO_VOLUME_STORAGE_KEY, browserAudioVolume.toFixed(2));
    } catch {
      // Local storage is optional; the in-room value still works for this visit.
    }
  }, [browserAudioVolume]);

  useEffect(() => {
    return () => {
      if (browserAudioVolumePreviewEndTimeoutRef.current !== null) {
        window.clearTimeout(browserAudioVolumePreviewEndTimeoutRef.current);
        browserAudioVolumePreviewEndTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!localBrowserAudioTrack) return;

    const element = document.createElement('audio');
    element.autoplay = true;
    element.style.display = 'none';
    element.dataset.drawclubBrowserAudioMonitor = 'true';
    document.body.appendChild(element);
    localBrowserAudioTrack.attach(element);

    return () => {
      localBrowserAudioTrack.detach(element);
      element.remove();
    };
  }, [localBrowserAudioTrack]);

  const setLiveKitBrowserAudioOutputVolume = useCallback((nextVolume: number) => {
    const volume = clampBrowserAudioVolume(nextVolume);
    const isMuted = volume === 0;
    const participants = new Set<RemoteParticipant>();

    setRenderedLiveKitBrowserAudioVolume(volume);

    browserAudioTrackRefs.forEach((trackRef) => {
      if (trackRef.participant instanceof RemoteParticipant) {
        participants.add(trackRef.participant);
      }
    });

    localBrowserAudioTrack?.attachedElements.forEach((element) => {
      element.muted = isMuted;
      element.volume = volume;
    });

    participants.forEach((participant) => {
      participant.setVolume(volume, Track.Source.ScreenShareAudio);
    });
  }, [browserAudioTrackRefs, localBrowserAudioTrack]);

  useEffect(() => {
    logVideoDiagnostic('info', 'browser-audio-volume-target-changed', {
      userVolume: browserAudioVolume,
      targetVolume: liveKitBrowserAudioVolume,
      isDucking: shouldDuckBrowserAudio,
      effectiveIsDucking: effectiveShouldDuckBrowserAudio,
      isMicrophoneDucking: isDuckingMicrophoneSpeaking,
      decisionReason: browserAudioVolumeDecisionReason,
      isPreviewingRawVolume: shouldPreviewBrowserAudioRawVolume,
      remoteTrackCount: browserAudioTrackRefs.length,
      remoteTrackSignature: browserAudioTrackSignature,
      localTrack: localBrowserAudioTrack ? {
        sid: localBrowserAudioPublication?.trackSid ?? null,
        muted: localBrowserAudioPublication?.isMuted ?? null,
        attachedElements: localBrowserAudioTrack.attachedElements.length,
      } : null,
      shouldLowerForSessionState: shouldLowerBrowserAudioForSessionState,
      duckingMicTrackCount: duckingMicrophoneMediaTracks.length,
      duckingMicTrackSignature: duckingMicrophoneTrackSignature,
      settings: browserAudioSettings,
      isActiveDrawingPose,
      poseTimer: poseTimerDiagnostic,
      duckingMultiplier: browserAudioDuckingMultiplier,
    }, context);
  }, [
    browserAudioTrackRefs.length,
    browserAudioTrackSignature,
    browserAudioVolume,
    browserAudioSettings,
    browserAudioDuckingMultiplier,
    browserAudioVolumeDecisionReason,
    context,
    duckingMicrophoneMediaTracks.length,
    duckingMicrophoneTrackSignature,
    effectiveShouldDuckBrowserAudio,
    isDuckingMicrophoneSpeaking,
    liveKitBrowserAudioVolume,
    localBrowserAudioPublication?.isMuted,
    localBrowserAudioPublication?.trackSid,
    localBrowserAudioTrack,
    poseTimerDiagnostic,
    isActiveDrawingPose,
    shouldDuckBrowserAudio,
    shouldLowerBrowserAudioForSessionState,
    shouldPreviewBrowserAudioRawVolume,
  ]);

  useEffect(() => {
    const previousSnapshot = browserAudioVolumeTransitionRef.current;
    const transitionReason = getBrowserAudioVolumeTransitionReason(
      previousSnapshot,
      browserAudioVolumeTransitionSnapshot
    );
    const transitionDurationMs = getBrowserAudioFadeDurationMs({
      fromVolume: currentLiveKitBrowserAudioVolumeRef.current,
      toVolume: liveKitBrowserAudioVolume,
      reason: transitionReason,
    });

    browserAudioVolumeTransitionRef.current = browserAudioVolumeTransitionSnapshot;

    if (transitionReason !== 'manual-volume' && transitionReason !== 'refresh') {
      logVideoDiagnostic('info', 'browser-audio-volume-transition-started', {
        reason: transitionReason,
        durationMs: transitionDurationMs,
        fromVolume: currentLiveKitBrowserAudioVolumeRef.current,
        targetVolume: liveKitBrowserAudioVolume,
        userVolume: browserAudioVolume,
        isMicrophoneDucking: isDuckingMicrophoneSpeaking,
        decisionReason: browserAudioVolumeDecisionReason,
        shouldLowerForSessionState: shouldLowerBrowserAudioForSessionState,
        isPreviewingRawVolume: shouldPreviewBrowserAudioRawVolume,
        isActiveDrawingPose,
        settings: browserAudioSettings,
        poseTimer: poseTimerDiagnostic,
      }, context);
    }

    animateBrowserAudioOutputVolume({
      fromVolume: currentLiveKitBrowserAudioVolumeRef.current,
      toVolume: liveKitBrowserAudioVolume,
      durationMs: transitionDurationMs,
      applyVolume: setLiveKitBrowserAudioOutputVolume,
      animationFrameRef: liveKitBrowserAudioAnimationRef,
      currentVolumeRef: currentLiveKitBrowserAudioVolumeRef,
    });

    const delayedApply = window.setTimeout(() => {
      setLiveKitBrowserAudioOutputVolume(liveKitBrowserAudioVolume);
      logVideoDiagnostic('info', 'browser-audio-volume-applied', {
        transitionReason,
        transitionDurationMs,
        targetVolume: liveKitBrowserAudioVolume,
        isDucking: shouldDuckBrowserAudio,
        effectiveIsDucking: effectiveShouldDuckBrowserAudio,
        isMicrophoneDucking: isDuckingMicrophoneSpeaking,
        decisionReason: browserAudioVolumeDecisionReason,
        shouldLowerForSessionState: shouldLowerBrowserAudioForSessionState,
        isPreviewingRawVolume: shouldPreviewBrowserAudioRawVolume,
        remoteTrackCount: browserAudioTrackRefs.length,
        webAudioContextCount: browserAudioPlaybackContexts.size,
        remoteAttachedElements: browserAudioTrackRefs.reduce((count, trackRef) => {
          return count + (trackRef.publication.track?.attachedElements.length ?? 0);
        }, 0),
        localAttachedElements: localBrowserAudioTrack?.attachedElements.length ?? 0,
        settings: browserAudioSettings,
        isActiveDrawingPose,
        poseTimer: poseTimerDiagnostic,
      }, context);
    }, transitionDurationMs + 150);

    return () => {
      if (liveKitBrowserAudioAnimationRef.current !== null) {
        window.cancelAnimationFrame(liveKitBrowserAudioAnimationRef.current);
        liveKitBrowserAudioAnimationRef.current = null;
      }
      window.clearTimeout(delayedApply);
    };
  }, [
    browserAudioTrackRefs,
    browserAudioVolume,
    browserAudioSettings,
    browserAudioVolumeDecisionReason,
    browserAudioVolumeTransitionSnapshot,
    context,
    effectiveShouldDuckBrowserAudio,
    isActiveDrawingPose,
    isDuckingMicrophoneSpeaking,
    liveKitBrowserAudioVolume,
    localBrowserAudioTrack,
    poseTimerDiagnostic,
    setLiveKitBrowserAudioOutputVolume,
    shouldDuckBrowserAudio,
    shouldLowerBrowserAudioForSessionState,
    shouldPreviewBrowserAudioRawVolume,
  ]);

  useEffect(() => {
    if (!hasBrowserAudio) {
      setShowBrowserAudioVolume(false);
    }
  }, [hasBrowserAudio]);

  const toggleMic = async () => {
    if (!localParticipant) return;
    logVideoDiagnostic('info', 'microphone-toggle-requested', {
      fromMuted: isMicMuted,
      toEnabled: isMicMuted,
      currentPublication: audioTrack
        ? {
            trackSid: audioTrack.trackSid,
            isMuted: audioTrack.isMuted,
            hasTrack: Boolean(audioTrack.track),
          }
        : null,
    }, context);
    try {
      await localParticipant.setMicrophoneEnabled(isMicMuted);
      logVideoDiagnostic('info', 'microphone-toggle-finished', {
        enabled: isMicMuted,
      }, context);
    } catch (error) {
      logVideoDiagnostic('error', 'microphone-toggle-failed', { error }, context);
      throw error;
    }
  };

  const toggleCamera = async () => {
    if (!localParticipant) return;
    logVideoDiagnostic('info', 'camera-toggle-requested', {
      fromOff: isCameraOff,
      toEnabled: isCameraOff,
      cameraProfile,
      modelParticipantId,
      modelProfileId,
      modelParticipantIds,
      participantProfileId,
      currentPublication: videoTrack
        ? {
            trackSid: videoTrack.trackSid,
            isMuted: videoTrack.isMuted,
            hasTrack: Boolean(videoTrack.track),
            settings: videoTrack.track?.mediaStreamTrack?.getSettings(),
          }
        : null,
    }, context);
    try {
      if (isCameraOff) {
        const requestedCameraDeviceId = getPreferredCameraDeviceId() || actualCameraDeviceId;
        const { captureOptions, publishOptions } = getCameraEnableOptions(
          cameraProfile,
          DEFAULT_HI_RES_TUNING_SETTINGS,
          requestedCameraDeviceId
        );
        await localParticipant.setCameraEnabled(true, captureOptions, publishOptions);
        const actualDevice = requestedCameraDeviceId
          ? await waitForPublishedCameraDevice(localParticipant, requestedCameraDeviceId)
          : getPublishedCameraDevice(localParticipant);
        logVideoDiagnostic(actualDevice.deviceId && requestedCameraDeviceId && actualDevice.deviceId !== requestedCameraDeviceId ? 'warn' : 'info', 'camera-toggle-published-device-resolved', {
          requestedDeviceId: requestedCameraDeviceId,
          requestedDeviceLabel: getMediaDeviceLabel(cameras, requestedCameraDeviceId),
          actualDeviceId: actualDevice.deviceId,
          actualDeviceLabel: actualDevice.label,
          actualWidth: actualDevice.width,
          actualHeight: actualDevice.height,
          actualFrameRate: actualDevice.frameRate,
        }, context);
      } else {
        await localParticipant.setCameraEnabled(false);
      }
      logVideoDiagnostic('info', 'camera-toggle-finished', {
        enabled: isCameraOff,
        cameraProfile,
        modelParticipantId,
        modelProfileId,
        modelParticipantIds,
        participantProfileId,
      }, context);
    } catch (error) {
      logVideoDiagnostic('error', 'camera-toggle-failed', { error }, context);
      throw error;
    }
  };

  const enableAudio = async () => {
    if (!room) return;
    try {
      await room.startAudio();
      resumeBrowserAudioPlaybackContexts();
      setCanPlayAudio(true);
      logVideoDiagnostic('info', 'audio-playback-started-by-user', null, context);
    } catch (error) {
      console.error('Failed to enable audio:', error);
      logVideoDiagnostic('error', 'audio-playback-start-failed', { error }, context);
    }
  };

  const handleMicChange = async (deviceId: string) => {
    setSelectedMic(deviceId);
    setShowMicMenu(false);
    try {
      await room.switchActiveDevice('audioinput', deviceId);
      setPreferredMicrophoneDeviceId(deviceId);
      logVideoDiagnostic('info', 'microphone-device-switched', {
        hasDeviceId: Boolean(deviceId),
      }, context);
    } catch (error) {
      console.error('Error switching microphone:', error);
      logVideoDiagnostic('error', 'microphone-device-switch-failed', { error }, context);
    }
  };

  const handleCameraChange = async (deviceId: string) => {
    setShowCameraMenu(false);
    setPendingCameraDeviceId(deviceId);
    dispatchCameraDeviceSwitchStarted();
    try {
      await room.switchActiveDevice('videoinput', deviceId, true);
      setPreferredCameraDeviceId(deviceId);
      const actualDevice = await waitForPublishedCameraDevice(localParticipant, deviceId);
      logVideoDiagnostic(actualDevice.deviceId === deviceId ? 'info' : 'warn', 'camera-device-switched', {
        requestedDeviceId: deviceId,
        requestedDeviceLabel: getMediaDeviceLabel(cameras, deviceId),
        actualDeviceId: actualDevice.deviceId,
        actualDeviceLabel: actualDevice.label,
        actualWidth: actualDevice.width,
        actualHeight: actualDevice.height,
        actualFrameRate: actualDevice.frameRate,
      }, context);
      dispatchCameraDeviceTrackRefreshed();
    } catch (error) {
      const actualDevice = getPublishedCameraDevice(localParticipant);
      console.error('Error switching camera:', error);
      logVideoDiagnostic('error', 'camera-device-switch-failed', {
        error,
        requestedDeviceId: deviceId,
        requestedDeviceLabel: getMediaDeviceLabel(cameras, deviceId),
        actualDeviceId: actualDevice.deviceId,
        actualDeviceLabel: actualDevice.label,
      }, context);
    } finally {
      setPendingCameraDeviceId(null);
      dispatchCameraDeviceSwitchFinished();
    }
  };

  const toggleBrowserAudioVolume = () => {
    resumeBrowserAudioPlaybackContexts();
    setShowBrowserAudioVolume((isOpen) => !isOpen);
    setShowMicMenu(false);
    setShowCameraMenu(false);
  };

  const clearBrowserAudioVolumePreviewEndTimeout = () => {
    if (browserAudioVolumePreviewEndTimeoutRef.current === null) return;

    window.clearTimeout(browserAudioVolumePreviewEndTimeoutRef.current);
    browserAudioVolumePreviewEndTimeoutRef.current = null;
  };

  const startBrowserAudioVolumePreview = (
    trigger: BrowserAudioVolumePreviewTrigger,
    previewVolume = browserAudioVolume
  ) => {
    clearBrowserAudioVolumePreviewEndTimeout();

    if (browserAudioVolumePreviewActiveRef.current) return;

    const clampedPreviewVolume = clampBrowserAudioVolume(previewVolume);
    browserAudioVolumePreviewActiveRef.current = true;
    setIsPreviewingBrowserAudioVolume(true);
    logVideoDiagnostic('info', 'browser-audio-volume-preview-started', {
      trigger,
      suppressionActive: shouldDuckBrowserAudio,
      suppressedTargetVolume: liveKitBrowserAudioVolume,
      previewTargetVolume: browserAudioMuted ? 0 : clampedPreviewVolume,
      userVolume: clampedPreviewVolume,
      isMicrophoneDucking: isDuckingMicrophoneSpeaking,
      shouldLowerForSessionState: shouldLowerBrowserAudioForSessionState,
      isActiveDrawingPose,
      remoteTrackCount: browserAudioTrackRefs.length,
      localTrackPresent: Boolean(localBrowserAudioTrack),
    }, context);
  };

  const finishBrowserAudioVolumePreview = (
    trigger: BrowserAudioVolumePreviewTrigger,
    delayMs = BROWSER_AUDIO_VOLUME_PREVIEW_HOLD_MS
  ) => {
    clearBrowserAudioVolumePreviewEndTimeout();

    if (!browserAudioVolumePreviewActiveRef.current) return;

    browserAudioVolumePreviewEndTimeoutRef.current = window.setTimeout(() => {
      browserAudioVolumePreviewEndTimeoutRef.current = null;
      browserAudioVolumePreviewActiveRef.current = false;
      setIsPreviewingBrowserAudioVolume(false);
      logVideoDiagnostic('info', 'browser-audio-volume-preview-ended', {
        trigger,
        suppressionActive: shouldDuckBrowserAudio,
        suppressedTargetVolume: browserAudioMuted
          ? 0
          : browserAudioVolume * (shouldDuckBrowserAudio ? BROWSER_AUDIO_DUCKED_VOLUME_MULTIPLIER : 1),
        userVolume: browserAudioVolume,
        isMicrophoneDucking: isDuckingMicrophoneSpeaking,
        shouldLowerForSessionState: shouldLowerBrowserAudioForSessionState,
        isActiveDrawingPose,
      }, context);
    }, delayMs);
  };

  const handleBrowserAudioVolumePreviewStart = (trigger: BrowserAudioVolumePreviewTrigger) => {
    resumeBrowserAudioPlaybackContexts();
    startBrowserAudioVolumePreview(trigger);
  };

  const handleBrowserAudioVolumePreviewEnd = (trigger: BrowserAudioVolumePreviewTrigger) => {
    finishBrowserAudioVolumePreview(trigger, BROWSER_AUDIO_VOLUME_PREVIEW_RELEASE_MS);
  };

  const handleBrowserAudioVolumeChange = (event: ChangeEvent<HTMLInputElement>) => {
    resumeBrowserAudioPlaybackContexts();
    const nextVolume = clampBrowserAudioVolume(Number(event.currentTarget.value) / 100);
    startBrowserAudioVolumePreview('slider-change', nextVolume);
    finishBrowserAudioVolumePreview('slider-change');
    logVideoDiagnostic('info', 'browser-audio-volume-slider-changed', {
      fromVolume: browserAudioVolume,
      toVolume: nextVolume,
      suppressionActive: shouldDuckBrowserAudio,
      previewingRawVolume: true,
      remoteTrackCount: browserAudioTrackRefs.length,
      localTrackPresent: Boolean(localBrowserAudioTrack),
    }, context);
    setBrowserAudioVolume(nextVolume);
  };

  const activeControlClassName = 'bg-black/55 border-white/20 text-white hover:bg-black/70';
  const inactiveControlClassName =
    'bg-white border-white text-black hover:bg-white shadow-[0_0_0_2px_rgba(255,255,255,0.55),0_10px_26px_rgba(0,0,0,0.42)]';
  const micControlClassName = isMicMuted ? inactiveControlClassName : activeControlClassName;
  const cameraControlClassName = isCameraOff ? inactiveControlClassName : activeControlClassName;
  const browserAudioControlClassName = browserAudioMuted ? inactiveControlClassName : activeControlClassName;
  const shouldShowBrowserAudioBreakQuietHint =
    showBrowserAudioVolume &&
    shouldLowerBrowserAudioForSessionState &&
    !browserAudioMuted;

  return (
    <div className={`pointer-events-none grid w-[min(100vw-1.5rem,34rem)] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-2 ${className}`}>
      <div className="hidden" aria-hidden="true">
        {browserAudioTrackRefs.map((trackRef) => (
          <BrowserAudioTrackPlayer
            key={`${trackRef.participant.identity}-${trackRef.publication.trackSid ?? trackRef.publication.trackName}`}
            trackRef={trackRef}
            volume={renderedLiveKitBrowserAudioVolume}
            muted={browserAudioMuted}
            context={context}
          />
        ))}
      </div>

      <div className="pointer-events-none flex min-w-0 justify-end gap-2">
        {/* Enable Audio button - shown when Chrome blocks audio */}
        {!canPlayAudio && (
          <button
            onClick={enableAudio}
            className="pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors animate-pulse"
          >
            <SpeakerIcon className="w-4 h-4" />
            Enable Audio
          </button>
        )}

        {hasBrowserAudio && (
          <div className="pointer-events-auto relative" ref={browserAudioControlRef}>
            <div className="flex rounded-lg border border-white/20 overflow-hidden">
              <button
                type="button"
                onClick={toggleBrowserAudioVolume}
                className={`
                  flex h-11 w-11 items-center justify-center backdrop-blur-md transition-colors
                  ${browserAudioControlClassName}
                `}
                aria-expanded={showBrowserAudioVolume}
                aria-haspopup="dialog"
                aria-label={`Music volume ${browserAudioVolumePercent}%. ${showBrowserAudioVolume ? 'Close' : 'Open'} music controls.`}
                title="Music volume"
              >
                <MusicIcon className="h-5 w-5 flex-shrink-0" />
              </button>
            </div>

            {showBrowserAudioVolume && (
              <div
                className="absolute bottom-full left-1/2 z-50 mb-2 flex w-20 -translate-x-1/2 flex-col items-center gap-2 rounded-lg border border-white/20 bg-black/90 p-2 text-white shadow-xl backdrop-blur-md"
                role="dialog"
                aria-label="Music volume control"
              >
                <div className="relative h-40 w-14">
                  {shouldShowBrowserAudioBreakQuietHint && (
                    <div
                      className="pointer-events-none absolute left-full top-0 ml-[30px] w-56 max-w-[calc(100vw-1.5rem)] rounded-lg border border-white/20 bg-black/90 px-3 py-2 text-[12px] font-medium leading-snug text-white shadow-xl backdrop-blur-md"
                      role="alert"
                    >
                      {BROWSER_AUDIO_BREAK_QUIET_HINT}
                    </div>
                  )}
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={browserAudioVolumePercent}
                    onChange={handleBrowserAudioVolumeChange}
                    onPointerDown={() => handleBrowserAudioVolumePreviewStart('slider-pointer-start')}
                    onPointerUp={() => handleBrowserAudioVolumePreviewEnd('slider-pointer-end')}
                    onPointerCancel={() => handleBrowserAudioVolumePreviewEnd('slider-pointer-end')}
                    onTouchStart={() => handleBrowserAudioVolumePreviewStart('slider-touch-start')}
                    onTouchEnd={() => handleBrowserAudioVolumePreviewEnd('slider-touch-end')}
                    onTouchCancel={() => handleBrowserAudioVolumePreviewEnd('slider-touch-end')}
                    onBlur={() => handleBrowserAudioVolumePreviewEnd('slider-blur')}
                    className="browser-audio-slider browser-audio-slider--vertical"
                    aria-label="Music volume"
                  />
                </div>
                <span className="w-full text-center text-[11px] font-semibold tabular-nums tracking-normal text-white/80">
                  {browserAudioVolumePercent}%
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mic control with dropdown */}
      <div className="pointer-events-auto flex items-end justify-center gap-2">
        <div className="relative" ref={micMenuRef}>
          {showMutedSpeakingWarning && (
            <MutedSpeakingTooltip visible={isMicMuted && mutedMicrophoneSpeaking} targetRef={micButtonRef} />
          )}
          <div className="flex rounded-lg border border-white/20 overflow-hidden">
            {/* Main mic toggle button */}
            <button
              ref={micButtonRef}
              onClick={toggleMic}
              className={`
                flex h-11 w-11 items-center justify-center border-r backdrop-blur-md transition-colors
                ${micControlClassName}
              `}
              aria-label={isMicMuted ? 'Microphone muted. Click to unmute.' : 'Microphone on. Click to mute.'}
              title={isMicMuted ? 'Unmute microphone' : 'Mute microphone'}
            >
              {isMicMuted ? (
                <MicOffIcon className="w-5 h-5" />
              ) : (
                <MicIcon className="w-5 h-5" />
              )}
            </button>

            {/* Dropdown arrow */}
            <button
              onClick={() => {
                setShowMicMenu(!showMicMenu);
                setShowCameraMenu(false);
              }}
              className={`
                flex h-11 w-7 items-center justify-center backdrop-blur-md transition-colors border-l-0
                ${micControlClassName}
              `}
              title="Select microphone"
            >
              <ChevronDownIcon className="w-3 h-3" />
            </button>
          </div>

          {/* Mic dropdown menu */}
          {showMicMenu && (
            <div className="absolute bottom-full left-0 mb-2 w-64 bg-black/95 backdrop-blur-md border border-white/20 rounded-lg shadow-xl overflow-hidden z-50 font-sans">
            <div className="px-3 py-2 text-xs text-white/50 border-b border-white/10 font-medium">
              Select Microphone
            </div>
            <div className="max-h-48 overflow-y-auto scrollbar-dark">
              {microphones.length === 0 ? (
                <div className="px-3 py-2 text-sm text-white/50">No microphones found</div>
              ) : (
                microphones.map((mic) => (
                  <button
                    key={mic.deviceId}
                    onClick={() => handleMicChange(mic.deviceId)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors flex items-center gap-2 ${
                      selectedMic === mic.deviceId ? 'text-green-400' : 'text-white'
                    }`}
                  >
                    {selectedMic === mic.deviceId && <CheckIcon className="w-4 h-4 flex-shrink-0" />}
                    <span className={selectedMic === mic.deviceId ? '' : 'ml-6'}>
                      {mic.label || `Microphone ${microphones.indexOf(mic) + 1}`}
                    </span>
                  </button>
                ))
              )}
            </div>
            </div>
          )}
        </div>

        {/* Camera control with dropdown */}
        <div className="relative" ref={cameraMenuRef}>
          <div className="flex rounded-lg border border-white/20 overflow-hidden">
            {/* Main camera toggle button */}
            <button
              onClick={toggleCamera}
              className={`
                flex h-11 w-11 items-center justify-center border-r backdrop-blur-md transition-colors
                ${cameraControlClassName}
              `}
              aria-label={isCameraOff ? 'Camera off. Click to turn on.' : 'Camera on. Click to turn off.'}
              title={isCameraOff ? 'Turn on camera' : 'Turn off camera'}
            >
              {isCameraOff ? (
                <CameraOffIcon className="w-5 h-5" />
              ) : (
                <CameraIcon className="w-5 h-5" />
              )}
            </button>

            {/* Dropdown arrow */}
            <button
              onClick={() => {
                setShowCameraMenu(!showCameraMenu);
                setShowMicMenu(false);
              }}
              className={`
                flex h-11 w-7 items-center justify-center backdrop-blur-md transition-colors border-l-0
                ${cameraControlClassName}
              `}
              title="Select camera"
            >
              <ChevronDownIcon className="w-3 h-3" />
            </button>
          </div>

          {/* Camera dropdown menu */}
          {showCameraMenu && (
            <div className="absolute bottom-full left-0 mb-2 w-64 bg-black/95 backdrop-blur-md border border-white/20 rounded-lg shadow-xl overflow-hidden z-50 font-sans">
            <div className="px-3 py-2 text-xs text-white/50 border-b border-white/10 font-medium">
              Select Camera
            </div>
            <div className="max-h-48 overflow-y-auto scrollbar-dark">
              {cameras.length === 0 ? (
                <div className="px-3 py-2 text-sm text-white/50">No cameras found</div>
              ) : (
                cameras.map((camera) => (
                  <button
                    key={camera.deviceId}
                    onClick={() => handleCameraChange(camera.deviceId)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors flex items-center gap-2 ${
                      actualCameraDeviceId === camera.deviceId ? 'text-green-400' : 'text-white'
                    }`}
                  >
                    {actualCameraDeviceId === camera.deviceId && <CheckIcon className="w-4 h-4 flex-shrink-0" />}
                    <span className={actualCameraDeviceId === camera.deviceId ? '' : 'ml-6'}>
                      {camera.label || `Camera ${cameras.indexOf(camera) + 1}`}
                      {pendingCameraDeviceId === camera.deviceId && (
                        <span className="ml-2 text-xs text-white/45">switching</span>
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
            </div>
          )}
        </div>
      </div>

      <div className="pointer-events-none flex min-w-0 justify-start">
        {canPinLocalCamera && onToggleLocalCameraPin && (
          <button
            type="button"
            onClick={onToggleLocalCameraPin}
            className={`
              pointer-events-auto flex h-11 items-center gap-1.5 rounded-lg border border-white/20 px-3 text-[11px] font-semibold uppercase tracking-normal backdrop-blur-md transition-colors
              ${activeControlClassName}
            `}
            aria-pressed={isLocalCameraPinned}
          >
            {isLocalCameraPinned ? 'Unpin me' : 'Pin me'}
          </button>
        )}
      </div>
    </div>
  );
}

function BrowserAudioTrackPlayer({
  trackRef,
  volume,
  muted,
  context,
}: {
  trackRef: TrackReference;
  volume: number;
  muted: boolean;
  context: VideoDiagnosticContext;
}) {
  const gainNodeRef = useRef<GainNode | null>(null);
  const lastGainDiagnosticRef = useRef<{ volume: number; muted: boolean; loggedAt: number } | null>(null);
  const mediaStreamTrack = trackRef.publication.track?.mediaStreamTrack ?? null;
  const canUseWebAudio = Boolean(getAudioContextConstructor());
  const outputVolume = muted ? 0 : clampBrowserAudioVolume(volume);

  useEffect(() => {
    if (!mediaStreamTrack) {
      logVideoDiagnostic('warn', 'browser-audio-web-audio-track-missing', {
        participantId: trackRef.participant.identity,
        trackSid: trackRef.publication.trackSid ?? null,
        trackName: trackRef.publication.trackName,
        publicationMuted: trackRef.publication.isMuted,
      }, context);
      return;
    }

    if (mediaStreamTrack.readyState !== 'live') {
      logVideoDiagnostic('warn', 'browser-audio-web-audio-track-not-live', {
        participantId: trackRef.participant.identity,
        trackSid: trackRef.publication.trackSid ?? null,
        trackName: trackRef.publication.trackName,
        readyState: mediaStreamTrack.readyState,
      }, context);
      return;
    }

    if (!canUseWebAudio) {
      logVideoDiagnostic('warn', 'browser-audio-web-audio-unavailable-using-audio-element', {
        participantId: trackRef.participant.identity,
        trackSid: trackRef.publication.trackSid ?? null,
        trackName: trackRef.publication.trackName,
      }, context);
      return;
    }

    const AudioContextConstructor = getAudioContextConstructor();
    if (!AudioContextConstructor) return;

    const audioContext = new AudioContextConstructor();
    const handleAudioContextStateChange = () => {
      logVideoDiagnostic('info', 'browser-audio-web-audio-context-state-changed', {
        participantId: trackRef.participant.identity,
        trackSid: trackRef.publication.trackSid ?? null,
        state: audioContext.state,
      }, context);
    };

    audioContext.addEventListener('statechange', handleAudioContextStateChange);
    const stream = new MediaStream([mediaStreamTrack]);
    const sourceNode = audioContext.createMediaStreamSource(stream);
    const gainNode = audioContext.createGain();

    gainNode.gain.value = 0;
    sourceNode.connect(gainNode);
    gainNode.connect(audioContext.destination);
    gainNodeRef.current = gainNode;
    browserAudioPlaybackContexts.add(audioContext);

    logVideoDiagnostic('info', 'browser-audio-web-audio-player-started', {
      participantId: trackRef.participant.identity,
      trackSid: trackRef.publication.trackSid ?? null,
      trackName: trackRef.publication.trackName,
      trackLabel: mediaStreamTrack.label,
      trackEnabled: mediaStreamTrack.enabled,
      trackMuted: mediaStreamTrack.muted,
      trackReadyState: mediaStreamTrack.readyState,
      audioContextState: audioContext.state,
      webAudioContextCount: browserAudioPlaybackContexts.size,
    }, context);

    if (audioContext.state === 'suspended') {
      void audioContext.resume()
        .then(() => {
          logVideoDiagnostic('info', 'browser-audio-web-audio-resume-requested', {
            participantId: trackRef.participant.identity,
            trackSid: trackRef.publication.trackSid ?? null,
            state: audioContext.state,
          }, context);
        })
        .catch((error) => {
          logVideoDiagnostic('warn', 'browser-audio-web-audio-resume-failed', { error }, context);
        });
    }

    return () => {
      browserAudioPlaybackContexts.delete(audioContext);
      gainNodeRef.current = null;
      audioContext.removeEventListener('statechange', handleAudioContextStateChange);
      sourceNode.disconnect();
      gainNode.disconnect();
      void audioContext.close().catch(() => undefined);
      logVideoDiagnostic('info', 'browser-audio-web-audio-player-stopped', {
        participantId: trackRef.participant.identity,
        trackSid: trackRef.publication.trackSid ?? null,
        webAudioContextCount: browserAudioPlaybackContexts.size,
      }, context);
    };
  }, [
    canUseWebAudio,
    context,
    mediaStreamTrack,
    trackRef.participant.identity,
    trackRef.publication.isMuted,
    trackRef.publication.trackName,
    trackRef.publication.trackSid,
  ]);

  useEffect(() => {
    if (!canUseWebAudio || !mediaStreamTrack) return;

    const gainNode = gainNodeRef.current;
    if (!gainNode) {
      logVideoDiagnostic('warn', 'browser-audio-web-audio-gain-missing', {
        participantId: trackRef.participant.identity,
        trackSid: trackRef.publication.trackSid ?? null,
        requestedVolume: outputVolume,
        muted,
        canUseWebAudio,
      }, context);
      return;
    }

    const audioContext = gainNode.context;
    gainNode.gain.setTargetAtTime(
      outputVolume,
      audioContext.currentTime,
      BROWSER_AUDIO_WEB_AUDIO_GAIN_TIME_CONSTANT_SECONDS
    );
    const now = Date.now();
    const lastDiagnostic = lastGainDiagnosticRef.current;
    const shouldLogGain =
      !lastDiagnostic ||
      lastDiagnostic.muted !== muted ||
      Math.abs(lastDiagnostic.volume - outputVolume) >= 0.05 ||
      now - lastDiagnostic.loggedAt >= 1000;

    if (shouldLogGain) {
      lastGainDiagnosticRef.current = { volume: outputVolume, muted, loggedAt: now };
      logVideoDiagnostic('info', 'browser-audio-web-audio-gain-applied', {
        participantId: trackRef.participant.identity,
        trackSid: trackRef.publication.trackSid ?? null,
        requestedVolume: volume,
        outputVolume,
        muted,
        audioContextState: audioContext.state,
        currentGainValue: gainNode.gain.value,
        targetGainValue: outputVolume,
      }, context);
    }
  }, [
    canUseWebAudio,
    context,
    mediaStreamTrack,
    muted,
    outputVolume,
    trackRef.participant.identity,
    trackRef.publication.trackSid,
    volume,
  ]);

  if (!canUseWebAudio) {
    return <AudioTrack trackRef={trackRef} volume={volume} muted={muted} />;
  }

  return null;
}

function usePoseAutoMuteArtists({
  enabled,
  localParticipant,
  isMicMuted,
  context,
}: {
  enabled: boolean;
  localParticipant: LocalParticipant | undefined;
  isMicMuted: boolean;
  context: VideoDiagnosticContext;
}) {
  const autoMuteActiveRef = useRef(false);
  const previousMicEnabledRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!localParticipant) return;

    const currentMicEnabled = !isMicMuted;

    if (enabled && !autoMuteActiveRef.current) {
      autoMuteActiveRef.current = true;
      previousMicEnabledRef.current = currentMicEnabled;
      logVideoDiagnostic('info', 'pose-auto-mute-started', {
        previousMicEnabled: currentMicEnabled,
      }, context);

      if (currentMicEnabled) {
        void localParticipant.setMicrophoneEnabled(false).catch((error) => {
          logVideoDiagnostic('warn', 'pose-auto-mute-failed', { error }, context);
        });
      }
      return;
    }

    if (!enabled && autoMuteActiveRef.current) {
      autoMuteActiveRef.current = false;
      const previousMicEnabled = previousMicEnabledRef.current;
      previousMicEnabledRef.current = null;

      logVideoDiagnostic('info', 'pose-auto-mute-ended', {
        previousMicEnabled,
        currentMicEnabled,
      }, context);

      if (previousMicEnabled !== null) {
        void localParticipant.setMicrophoneEnabled(previousMicEnabled).catch((error) => {
          logVideoDiagnostic('warn', 'pose-auto-mute-restore-failed', { error }, context);
        });
      }
    }
  }, [context, enabled, isMicMuted, localParticipant]);

  useEffect(() => {
    return () => {
      if (!localParticipant || !autoMuteActiveRef.current) return;

      const previousMicEnabled = previousMicEnabledRef.current;
      autoMuteActiveRef.current = false;
      previousMicEnabledRef.current = null;

      if (previousMicEnabled !== null) {
        void localParticipant.setMicrophoneEnabled(previousMicEnabled).catch((error) => {
          logVideoDiagnostic('warn', 'pose-auto-mute-unmount-restore-failed', { error }, context);
        });
      }
    };
  }, [context, localParticipant]);
}

function useMutedMicrophoneSpeaking({
  enabled,
  deviceId,
  context,
}: {
  enabled: boolean;
  deviceId: string;
  context: VideoDiagnosticContext;
}) {
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setSpeaking(false);
      return;
    }

    if (typeof window === 'undefined' || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setSpeaking(false);
      return;
    }

    const AudioContextConstructor = getAudioContextConstructor();
    if (!AudioContextConstructor) {
      setSpeaking(false);
      return;
    }

    let cancelled = false;
    let stream: MediaStream | null = null;
    let audioContext: AudioContext | null = null;
    let sourceNode: MediaStreamAudioSourceNode | null = null;
    let intervalId: number | null = null;

    const startDetector = async () => {
      try {
        const audioConstraints: MediaTrackConstraints = {
          autoGainControl: false,
          echoCancellation: false,
          noiseSuppression: false,
        };

        if (deviceId) {
          audioConstraints.deviceId = { exact: deviceId };
        }

        stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const nextAudioContext = new AudioContextConstructor();
        audioContext = nextAudioContext;
        if (nextAudioContext.state === 'suspended') {
          await nextAudioContext.resume().catch(() => undefined);
        }

        const analyser = nextAudioContext.createAnalyser();
        analyser.fftSize = 1024;
        sourceNode = nextAudioContext.createMediaStreamSource(stream);
        sourceNode.connect(analyser);

        const samples = new Float32Array(analyser.fftSize);
        let speakingUntil = 0;

        intervalId = window.setInterval(() => {
          analyser.getFloatTimeDomainData(samples);

          let sum = 0;
          for (const sample of samples) {
            sum += sample * sample;
          }

          const rms = Math.sqrt(sum / samples.length);
          const now = Date.now();
          if (rms >= MUTED_MIC_SPEAKING_RMS_THRESHOLD) {
            speakingUntil = now + MUTED_MIC_SPEAKING_HOLD_MS;
          }

          const nextSpeaking = speakingUntil > now;
          setSpeaking((wasSpeaking) => wasSpeaking === nextSpeaking ? wasSpeaking : nextSpeaking);
        }, MUTED_MIC_ANALYSIS_INTERVAL_MS);
      } catch (error) {
        setSpeaking(false);
        logVideoDiagnostic('warn', 'muted-microphone-speaking-detector-failed', { error }, context);
      }
    };

    void startDetector();

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
      sourceNode?.disconnect();
      stream?.getTracks().forEach((track) => track.stop());
      void audioContext?.close().catch(() => undefined);
    };
  }, [context, deviceId, enabled]);

  return speaking;
}

function getAudioContextConstructor() {
  if (typeof window === 'undefined') return null;

  const audioWindow = window as Window & {
    webkitAudioContext?: typeof AudioContext;
  };
  const nativeAudioContext = typeof AudioContext === 'undefined' ? null : AudioContext;

  return nativeAudioContext ?? audioWindow.webkitAudioContext ?? null;
}

function MutedSpeakingTooltip({
  visible,
  targetRef,
}: {
  visible: boolean;
  targetRef: RefObject<HTMLButtonElement | null>;
}) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [isRendered, setIsRendered] = useState(false);
  const [isShown, setIsShown] = useState(false);
  const shownAtRef = useRef<number | null>(null);
  const showTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const unmountTimerRef = useRef<number | null>(null);

  const clearTooltipTimers = useCallback(() => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (unmountTimerRef.current !== null) {
      window.clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const target = targetRef.current;
    if (!target) {
      setPosition(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    setPosition({
      top: rect.top - 10,
      left: rect.left + rect.width / 2,
    });
  }, [targetRef]);

  useEffect(() => {
    if (!isRendered) {
      setPosition(null);
      return;
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isRendered, updatePosition]);

  useEffect(() => {
    if (visible) {
      clearTooltipTimers();
      setIsRendered(true);
      updatePosition();

      showTimerRef.current = window.setTimeout(() => {
        shownAtRef.current = Date.now();
        setIsShown(true);
        showTimerRef.current = null;
      }, MUTED_SPEAKING_TOOLTIP_SHOW_DELAY_MS);
      return;
    }

    if (!isRendered) return;

    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }

    const visibleForMs = shownAtRef.current === null ? 0 : Date.now() - shownAtRef.current;
    const hideDelayMs = Math.max(0, MUTED_SPEAKING_TOOLTIP_MIN_VISIBLE_MS - visibleForMs);

    hideTimerRef.current = window.setTimeout(() => {
      setIsShown(false);
      hideTimerRef.current = null;

      unmountTimerRef.current = window.setTimeout(() => {
        setIsRendered(false);
        setPosition(null);
        shownAtRef.current = null;
        unmountTimerRef.current = null;
      }, MUTED_SPEAKING_TOOLTIP_FADE_MS);
    }, hideDelayMs);
  }, [clearTooltipTimers, isRendered, updatePosition, visible]);

  useEffect(() => () => clearTooltipTimers(), [clearTooltipTimers]);

  if (!isRendered || !position || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="pointer-events-none fixed z-[9999] max-w-[220px] rounded-lg border border-white/15 bg-black px-3 py-2 text-center text-[12px] font-medium leading-snug text-white shadow-xl will-change-transform"
      style={{
        top: position.top,
        left: position.left,
        opacity: isShown ? 1 : 0,
        transform: isShown
          ? 'translate(-50%, -100%) translateY(-2px)'
          : 'translate(-50%, calc(-100% + 6px))',
        transition: `opacity ${MUTED_SPEAKING_TOOLTIP_FADE_MS}ms ease, transform ${MUTED_SPEAKING_TOOLTIP_FADE_MS}ms ease`,
      }}
      role="status"
      aria-live="polite"
      aria-hidden={!isShown}
    >
      Are you speaking? Unmute with this button
    </div>,
    document.body
  );
}

function animateBrowserAudioOutputVolume({
  fromVolume,
  toVolume,
  durationMs,
  applyVolume,
  animationFrameRef,
  currentVolumeRef,
}: {
  fromVolume: number;
  toVolume: number;
  durationMs: number;
  applyVolume: (volume: number) => void;
  animationFrameRef: { current: number | null };
  currentVolumeRef: { current: number };
}) {
  if (animationFrameRef.current !== null) {
    window.cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
  }

  const startVolume = clampBrowserAudioVolume(fromVolume);
  const endVolume = clampBrowserAudioVolume(toVolume);
  const duration = Math.max(0, durationMs);

  if (duration === 0) {
    currentVolumeRef.current = endVolume;
    applyVolume(endVolume);
    return;
  }

  const startedAt = performance.now();

  const tick = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    const easedProgress = 1 - Math.pow(1 - progress, 3);
    const nextVolume = startVolume + (endVolume - startVolume) * easedProgress;

    currentVolumeRef.current = nextVolume;
    applyVolume(nextVolume);

    if (progress < 1) {
      animationFrameRef.current = window.requestAnimationFrame(tick);
      return;
    }

    animationFrameRef.current = null;
    currentVolumeRef.current = endVolume;
    applyVolume(endVolume);
  };

  animationFrameRef.current = window.requestAnimationFrame(tick);
}

function getBrowserAudioFadeDurationMs({
  fromVolume,
  toVolume,
  reason,
}: {
  fromVolume: number;
  toVolume: number;
  reason: BrowserAudioVolumeTransitionReason;
}) {
  if (Math.abs(toVolume - fromVolume) < 0.01) {
    return 0;
  }

  if (reason === 'draw-break') {
    return BROWSER_AUDIO_DRAW_BREAK_FADE_MS;
  }

  if (reason === 'speech-duck') {
    return toVolume < fromVolume ? BROWSER_AUDIO_DUCK_ATTACK_MS : BROWSER_AUDIO_DUCK_RELEASE_MS;
  }

  if (reason === 'volume-preview') {
    return toVolume < fromVolume ? BROWSER_AUDIO_VOLUME_PREVIEW_RELEASE_MS : 0;
  }

  if (reason === 'manual-volume' || reason === 'initial' || reason === 'refresh') {
    return 0;
  }

  return toVolume < fromVolume ? BROWSER_AUDIO_DUCK_ATTACK_MS : BROWSER_AUDIO_DUCK_RELEASE_MS;
}

function getBrowserAudioVolumeDecisionReason({
  browserAudioMuted,
  isDuckingMicrophoneSpeaking,
  shouldLowerBrowserAudioForSessionState,
  shouldPreviewBrowserAudioRawVolume,
}: {
  browserAudioMuted: boolean;
  isDuckingMicrophoneSpeaking: boolean;
  shouldLowerBrowserAudioForSessionState: boolean;
  shouldPreviewBrowserAudioRawVolume: boolean;
}): BrowserAudioVolumeDecisionReason {
  if (browserAudioMuted) return 'muted';
  if (shouldPreviewBrowserAudioRawVolume) return 'raw-volume-preview';
  if (isDuckingMicrophoneSpeaking && shouldLowerBrowserAudioForSessionState) return 'speech-and-break-duck';
  if (isDuckingMicrophoneSpeaking) return 'speech-duck';
  if (shouldLowerBrowserAudioForSessionState) return 'break-duck';
  return 'normal';
}

function getBrowserAudioVolumeTransitionReason(
  previousSnapshot: BrowserAudioVolumeTransitionSnapshot | null,
  nextSnapshot: BrowserAudioVolumeTransitionSnapshot
): BrowserAudioVolumeTransitionReason {
  if (!previousSnapshot) {
    return 'initial';
  }

  if (
    previousSnapshot.shouldLowerForSessionState !== nextSnapshot.shouldLowerForSessionState ||
    previousSnapshot.isActiveDrawingPose !== nextSnapshot.isActiveDrawingPose
  ) {
    return 'draw-break';
  }

  if (previousSnapshot.isPreviewingRawVolume !== nextSnapshot.isPreviewingRawVolume) {
    return 'volume-preview';
  }

  if (previousSnapshot.isMicrophoneDucking !== nextSnapshot.isMicrophoneDucking) {
    return 'speech-duck';
  }

  if (Math.abs(previousSnapshot.userVolume - nextSnapshot.userVolume) >= 0.01) {
    return 'manual-volume';
  }

  if (Math.abs(previousSnapshot.targetVolume - nextSnapshot.targetVolume) >= 0.01) {
    return 'target-change';
  }

  return 'refresh';
}

function getStoredBrowserAudioVolume() {
  if (typeof localStorage === 'undefined') {
    return DEFAULT_BROWSER_AUDIO_VOLUME;
  }

  try {
    const storedValue = Number(localStorage.getItem(BROWSER_AUDIO_VOLUME_STORAGE_KEY));
    if (!Number.isFinite(storedValue)) {
      return DEFAULT_BROWSER_AUDIO_VOLUME;
    }

    return clampBrowserAudioVolume(storedValue);
  } catch {
    return DEFAULT_BROWSER_AUDIO_VOLUME;
  }
}

function clampBrowserAudioVolume(value: number) {
  if (!Number.isFinite(value)) {
    return DEFAULT_BROWSER_AUDIO_VOLUME;
  }

  return Math.min(1, Math.max(0, value));
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}

function MicOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 14a3 3 0 01-3-3V5a3 3 0 016 0v1m0 3.5V11a3 3 0 01-3 3z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 3l18 18"
      />
    </svg>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function CameraOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 3l18 18"
      />
    </svg>
  );
}

function SpeakerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M6 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h2l4-4v14l-4-4z"
      />
    </svg>
  );
}

function MusicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 18V6l10-2v12"
      />
      <circle cx="7" cy="18" r="2.5" strokeWidth={2} />
      <circle cx="17" cy="16" r="2.5" strokeWidth={2} />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
